import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  createAddress,
  deleteAddress,
  fetchAddresses,
  updateAddress,
  type CustomerAddress,
} from '../api/addressApi'

import type { EnvironmentWithChina } from '../App'

interface UseAddressesOptions {
  accountNumber: string
  env: EnvironmentWithChina
  authToken?: string
}

/**
 * The address book's read + write surface.
 *
 * Every mutation invalidates the list rather than patching the cache, so a
 * failed write leaves the rendered list exactly as it was (RP-5872 AC 9) and a
 * successful one always reflects what SFSC actually stored — including the
 * previous primary address it silently demoted.
 */
export function useAddresses({
  accountNumber,
  env,
  authToken,
}: UseAddressesOptions) {
  const queryClient = useQueryClient()
  const queryKey = ['addresses', accountNumber, env]

  const list = useQuery({
    queryKey,
    queryFn: () => fetchAddresses(accountNumber, env, authToken),
    enabled: Boolean(accountNumber),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  const save = useMutation({
    mutationFn: ({
      addressId,
      address,
    }: {
      addressId?: string
      address: CustomerAddress
    }) =>
      addressId
        ? updateAddress(accountNumber, addressId, address, env, authToken)
        : createAddress(accountNumber, address, env, authToken),
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: (addressId: string) =>
      deleteAddress(accountNumber, addressId, env, authToken),
    onSuccess: invalidate,
  })

  return { list, save, remove }
}
